import React, { useState, useEffect } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { Location } from '../../types';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface LocationTransferFormProps {
  productId: string;
  onTransferComplete?: () => void;
  onCancel?: () => void;
}

const LocationTransferForm: React.FC<LocationTransferFormProps> = ({
  productId,
  onTransferComplete,
  onCancel
}) => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [fromLocation, setFromLocation] = useState<string>('');
  const [toLocation, setToLocation] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  
  const [showFromOptions, setShowFromOptions] = useState<boolean>(false);
  const [showToOptions, setShowToOptions] = useState<boolean>(false);
  const [showReasonOptions, setShowReasonOptions] = useState<boolean>(false);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setError('Failed to load locations');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!fromLocation) {
      setError('Please select a source location');
      return;
    }
    
    if (!toLocation) {
      setError('Please select a destination location');
      return;
    }
    
    if (fromLocation === toLocation) {
      setError('Source and destination locations cannot be the same');
      return;
    }
    
    if (quantity <= 0) {
      setError('Quantity must be greater than zero');
      return;
    }
    
    setLoading(true);
    
    try {
      const { data, error } = await supabase.rpc('transfer_inventory', {
        p_user_id: user?.id,
        p_product_id: productId,
        p_from_location_id: fromLocation,
        p_to_location_id: toLocation,
        p_quantity: quantity,
        p_reason: reason,
        p_notes: notes
      });

      if (error) throw error;
      
      setSuccess('Transfer completed successfully');
      setTimeout(() => {
        onTransferComplete?.();
      }, 1500);
    } catch (error: any) {
      console.error('Transfer error:', error);
      setError(error.message || 'Failed to transfer inventory');
    } finally {
      setLoading(false);
    }
  };

  const transferReasons = [
    { value: 'restock', label: 'Restocking' },
    { value: 'demand', label: 'High Demand' },
    { value: 'maintenance', label: 'Location Maintenance' },
    { value: 'optimization', label: 'Inventory Optimization' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transfer Inventory</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{success}</span>
            </div>
          )}
          
          {/* From Location Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Location
            </label>
            <div className="relative">
              <button
                type="button"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-left"
                onClick={() => setShowFromOptions(!showFromOptions)}
              >
                {fromLocation ? locations.find(l => l.id === fromLocation)?.name : 'Select location'}
              </button>
              {showFromOptions && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                  {locations.map(location => (
                    <div
                      key={location.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setFromLocation(location.id);
                        setShowFromOptions(false);
                      }}
                    >
                      {location.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!fromLocation && (
              <p className="mt-1 text-xs text-red-500">Source location is required</p>
            )}
          </div>
          
          {/* To Location Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Location
            </label>
            <div className="relative">
              <button
                type="button"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-left"
                onClick={() => setShowToOptions(!showToOptions)}
              >
                {toLocation ? locations.find(l => l.id === toLocation)?.name : 'Select location'}
              </button>
              {showToOptions && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                  {locations.map(location => (
                    <div
                      key={location.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setToLocation(location.id);
                        setShowToOptions(false);
                      }}
                    >
                      {location.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!toLocation && (
              <p className="mt-1 text-xs text-red-500">Destination location is required</p>
            )}
          </div>

          <Input
            label="Quantity"
            type="number"
            min="1"
            value={quantity.toString()}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            required
            fullWidth
          />

          {/* Reason Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <div className="relative">
              <button
                type="button"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-left"
                onClick={() => setShowReasonOptions(!showReasonOptions)}
              >
                {reason ? transferReasons.find(r => r.value === reason)?.label : 'Select reason'}
              </button>
              {showReasonOptions && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                  {transferReasons.map(reasonOption => (
                    <div
                      key={reasonOption.value}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setReason(reasonOption.value);
                        setShowReasonOptions(false);
                      }}
                    >
                      {reasonOption.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading}
            disabled={!fromLocation || !toLocation || fromLocation === toLocation || quantity <= 0}
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Transfer
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default LocationTransferForm;